// LSP-20787 (D18, E12): rep-facing copy, tone and actions per SMS delivery state, reconciled with the
// Claude Design prototype on 2026-09-28. The glyph belongs to the TONE, not the state: every permanent
// failure shares one amber, and the label and the buttons tell them apart.
import moment from "moment-timezone";
import type { SmsDeliveryState } from "@/api/index.d";

export type SmsDeliveryTone = "neutral" | "positive" | "attention";
export type SmsDeliveryAction = "update-number-resend" | "send-email" | "resend-text";

export interface SmsDeliveryDisplay {
	tone: SmsDeliveryTone;
	label: string; // list pill
	tooltip: string; // list pill tooltip; "{time}" → formatDeliverySince(since)
	sentence: string; // session header chip; "{time}" → formatDeliverySince(since)
	cardSentence: string; // scheduled-call banner; "{when}" → the call's date and time
	actions: SmsDeliveryAction[];
	channel: "sms" | "email"; // the list pill's trailing glyph
}

// The contact a repair edits: GET /conversations/:id → Customers[0]. Rows carry their id, createdAt and updatedAt at
// runtime (the shared PhoneNumber type omits them): PUT /customers/:id needs the id to edit a row in place, and the
// timestamps name the row the resend uses (contact-api controllers/conversations.ts syncParticipantContactFromCustomer).
export interface SmsDeliveryRepairCustomer {
	id: string;
	updatedAt: string | Date | null; // the API sends the ISO string; the shared Customer type calls it a Date
	PhoneNumbers?: Array<{ id?: string; phoneNumber: string; label?: string | null; createdAt?: string; updatedAt?: string }>;
	EmailAddresses?: Array<{ id?: string; emailAddress: string; label?: string | null; createdAt?: string }>;
}

// LSP-20787 (22016): the session's participants (GET /conversations/:id → Participants), so the repair box edits the
// contact row this session's invite actually went to.
export interface SmsDeliveryRepairParticipant {
	customerId?: string | null;
	phone?: string | null;
	createdAt?: string;
}

// Keys are the API's SMS_DELIVERY_STATES; a contact-api cross-app guard fails CI if the two drift.
export const SMS_DELIVERY_DISPLAY: Readonly<Record<SmsDeliveryState, SmsDeliveryDisplay>> = Object.freeze({
	checking: {
		tone: "neutral",
		label: "Checking…",
		tooltip: "Waiting for delivery confirmation",
		sentence: "Checking delivery…",
		cardSentence: "Sending invite… · Call {when}",
		actions: [],
		channel: "sms"
	},
	delivered: {
		tone: "positive",
		label: "Delivered",
		tooltip: "Text delivered · {time}",
		sentence: "Text delivered · {time}",
		cardSentence: "Invite delivered · Call {when}",
		actions: [],
		channel: "sms"
	},
	// E5 (§2.3) originally kept "check with the contact before resending" in the header sentence; E12 item 3
	// superseded that 2026-09-29 (Ankit) — the header sentence is just "Delivery not confirmed". B6: never a
	// guess about the phone — an unrecognised reason can be a provider-side failure. D20: an email after
	// Unconfirmed derives Emailed.
	unconfirmed: {
		tone: "neutral",
		label: "Unconfirmed",
		tooltip: "Sent, delivery not confirmed",
		sentence: "Delivery not confirmed",
		cardSentence: "Invite sent, delivery unconfirmed · Call {when}",
		actions: ["resend-text", "send-email"],
		channel: "sms"
	},
	// D11/E8: the provider never reports delivery — nothing to wait for, nothing to fix, and it is never named.
	not_tracked: {
		tone: "neutral",
		label: "Not tracked",
		tooltip: "Delivery isn't reported for this text",
		sentence: "Delivery isn't reported for this text",
		cardSentence: "Invite sent · Call {when}",
		actions: [],
		channel: "sms"
	},
	bad_number: {
		tone: "attention",
		label: "Text not delivered",
		tooltip: "Text not delivered — try another way",
		sentence: "Text not delivered — this number can't receive texts",
		cardSentence: "Invite didn't reach them — call is still set for {when}",
		actions: ["update-number-resend", "send-email"],
		channel: "sms"
	},
	// LSP-20787 §8 (Ankit 2026-10-15): AWS phone/carrier unreachable and Twilio 30003. In production none of these ever
	// delivered on a retry, so email leads; Update number with the same number still re-texts it.
	unreachable: {
		tone: "attention",
		label: "Carrier unreachable",
		tooltip: "Carrier unreachable — try another way",
		sentence: "Text not delivered — carrier unreachable",
		cardSentence: "Invite didn't reach them — call is still set for {when}",
		actions: ["send-email", "update-number-resend"],
		channel: "sms"
	},
	// E8: provider-neutral on purpose — the number a contact must text START to is not knowable from the code.
	not_accepting_texts: {
		tone: "attention",
		label: "Not accepting texts",
		// LSP-20787 (22026): no "email them instead" — the org may not allow email; the buttons offer it when it can.
		tooltip: "Not accepting texts — they opted out",
		sentence: "This contact isn't accepting texts — they opted out",
		cardSentence: "They aren't accepting texts — call is still set for {when}",
		actions: ["send-email"],
		channel: "sms"
	},
	// D12: email only — resending identical content into a carrier's spam filter escalates the block.
	carrier_block: {
		tone: "attention",
		label: "Text not delivered",
		tooltip: "Text not delivered — try another way",
		sentence: "Text not delivered — their carrier rejected it",
		cardSentence: "Invite didn't reach them — call is still set for {when}",
		actions: ["send-email"],
		channel: "sms"
	},
	// E2: the Datadog monitor is the admin's notification; the rep gets one line saying so.
	cant_text: {
		tone: "attention",
		label: "Can't text",
		tooltip: "We can't text this number — use another way",
		sentence: "We can't text this number yet — our team has been notified",
		cardSentence: "We can't text this number yet — call is still set for {when}",
		actions: ["send-email"],
		channel: "sms"
	},
	email_sent: {
		tone: "neutral",
		label: "Emailed",
		tooltip: "Invite sent by email · {time}",
		sentence: "Invite sent by email · {time}",
		cardSentence: "Invite sent by email · Call {when}",
		actions: [],
		channel: "email"
	},
	// LSP-22023: a cancellation notice newer than the invite text. Nothing to repair on a canceled call.
	canceled: {
		tone: "neutral",
		label: "Call canceled",
		tooltip: "Call canceled · {time}",
		sentence: "Call canceled · {time}",
		cardSentence: "Call for {when} canceled",
		actions: [],
		channel: "sms"
	},
	// LSP-22023: every cancellation notice failed (e.g. an opted-out number). Amber so the rep knows to tell them, but
	// still no actions — no repair, no Send via email and no sessions-list Fix (showDeliveryFix needs an action).
	canceled_not_notified: {
		tone: "attention",
		label: "Call canceled",
		tooltip: "Call canceled — they weren't notified",
		sentence: "Call canceled — the contact wasn't notified",
		cardSentence: "Call for {when} canceled — the contact wasn't notified",
		actions: [],
		channel: "sms"
	}
});

export const SMS_DELIVERY_ACTION_LABELS: Readonly<Record<SmsDeliveryAction, string>> = Object.freeze({
	"update-number-resend": "Update number & resend",
	"send-email": "Send via email",
	"resend-text": "Resend"
});

// Tone-level glyphs (D18): grey is a CSS dot (.sms-delivery__dot, src/styles/sms-delivery.scss), green a
// check, amber a warning triangle — both registered in src/iconsets/mdi.ts.
export const SMS_DELIVERY_TONE_ICONS: Readonly<Record<SmsDeliveryTone, string | null>> = Object.freeze({
	neutral: null,
	positive: "mdi-check",
	attention: "mdi-alert"
});

// Amber = the permanent failures (and a cancellation nobody was told about); the list offers Fix only for a state with
// an action the caller can take (D15, ConversationsPage showDeliveryFix).
export function isAttentionState(state: SmsDeliveryState | null | undefined): boolean {
	return state != null && SMS_DELIVERY_DISPLAY[state]?.tone === "attention";
}

// E9: the states whose only repair is an email (not accepting texts, carrier block, can't text). The generic
// "Resend Invitation" goes by email only for them — re-texting a blocked number is exactly what the table rules out.
export function isEmailOnlyRepairState(state: SmsDeliveryState | null | undefined): boolean {
	const actions = state != null ? SMS_DELIVERY_DISPLAY[state]?.actions : undefined;
	return actions?.length === 1 && actions[0] === "send-email";
}

// Final review I1: an organization with "Allow Email Invitations" off gets no email repair — the API refuses channel
// "email" there (400) — so every surface offers only what can go out; an empty list renders nothing and the list shows no
// Fix. The caller reads the setting the booking forms read: `me.organization.allowEmailInvitations !== false`.
export function availableDeliveryActions(
	state: SmsDeliveryState | null | undefined,
	{ emailInvitationsAllowed }: { emailInvitationsAllowed: boolean }
): SmsDeliveryAction[] {
	const actions = state != null ? (SMS_DELIVERY_DISPLAY[state]?.actions ?? []) : [];
	return actions.filter((action) => emailInvitationsAllowed || action !== "send-email");
}

// §6: the "· {time}" in delivered / email_sent copy — the time alone today, the date as well otherwise.
// LSP-21861: followed by the zone ("9:07 PM EDT"), so it can be read against the session's other times.
export function formatDeliverySince(since: string, now: Date = new Date(), timeZone: string = moment.tz.guess()): string {
	const sinceMoment = moment(since).tz(timeZone);
	const isToday = sinceMoment.isSame(moment(now).tz(timeZone), "day");
	return sinceMoment.format(isToday ? "h:mm A z" : "MMM D · h:mm A z");
}

// D4: once the contact has engaged with the session, whether the invite text landed no longer
// matters — and old finished sessions must not light up amber on release day.
const ENGAGED_STATUSES = new Set(["delivered", "inprogress", "processing", "readyforreview", "reviewed", "completed"]);

export function isDeliveryRelevantStatus(status: string | null | undefined): boolean {
	return !ENGAGED_STATUSES.has((status ?? "").toLowerCase());
}
